import { Box, Divider, Hidden, Page, PageBlock } from 'braid-design-system';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import CustomLoader from 'src/components/CustomLoader/CustomLoader';
import Header from 'src/components/Header/Header';
import { SiteWideFooter } from 'src/components/SiteWideFooter';
import { usePostListingById } from 'src/hooks/usePostListing';
import type { StoreProps } from 'src/store';
import { createPostListingUrl } from 'src/utils';

import Confirmation from './components/Confirmation';

const PostListing = () => {
  const { id: listingId } = useParams();
  const postListingById = usePostListingById();
  const clientId = useSelector((store: StoreProps) => store.account.id);

  useEffect(() => {
    const handlePostListingById = async () => {
      if (
        typeof listingId === 'string' &&
        postListingById.status.kind === 'idle'
      ) {
        const result = await postListingById.post(listingId);

        if (
          result.kind === 'error' &&
          result.error.type === 'account_pending_review'
        ) {
          // We expect private sellers on List Until Sold products to receive an email that would lead them to this component (via the url "/sell/listing/:id/repost")
          // Reference
          //  - https://github.com/SEEK-Jobs/sbiz-seek-business/blob/master/Libraries/Seek.Commercial.BusinessLayer/Emailing/ListUntilSoldEmailManager.cs#L26
          //  - Which is called from: https://github.com/SEEK-Jobs/sbiz-seek-business/blob/master/Applications/Seek.Commercial.Mail.ListingExpiryWarning/Program.cs#L191
          // If private seller is not trusted when posting listing,
          // Redirect them to the listing preview page on CASBA to opt into the standard pending listing flow
          const casbaPreviewPage = createPostListingUrl(
            clientId,
            Number(listingId),
          );
          window.location.assign(casbaPreviewPage);
          return;
        }
      }
    };

    handlePostListingById();
  }, [listingId, postListingById, clientId]);

  const params =
    postListingById.status.kind === 'success'
      ? {
          title: 'Congratulations',
          cardTitle: 'You have successfully posted your business for sale',
          body: 'Call customer service on 13 73 35 (Mon - Fri, 9am - 5pm AEST) if we can help you with anything else.',
        }
      : {
          title: 'Something went wrong',
          cardTitle: 'Your business for sale has not been posted',
          body: 'Contact customer service on 13 73 35 (Mon - Fri, 9am - 5pm AEST) for help.',
        };

  return (
    <Page footer={<SiteWideFooter />} footerPosition="belowFold">
      <Header title="Sell your business" />

      <Hidden below="tablet">
        <Divider />
      </Hidden>

      <Box paddingTop="xlarge">
        <PageBlock width="small">
          {postListingById.status.kind === 'loading' ? (
            <CustomLoader />
          ) : (
            <Confirmation {...params} />
          )}
        </PageBlock>
      </Box>
    </Page>
  );
};

export default PostListing;
